// Checklist items drawn with a checkbox in place of their `- [ ] ` marker.
// The text stays the source: a click on the box swaps the ' ' and the 'x',
// so it has the editor's undo history. The cursor steps over the box, and
// Backspace after it takes the marker, leaving a plain line.
import {
  findItems,
  type Item,
  nextItem,
  toggle,
} from '@/app/checklist/checklist';
import {
  EditorSelection,
  type EditorState,
  StateField,
} from '@codemirror/state';
import {
  type Command,
  Decoration,
  type DecorationSet,
  EditorView,
  keymap,
  WidgetType,
} from '@codemirror/view';

class CheckboxWidget extends WidgetType {
  constructor(readonly checked: boolean) {
    super();
  }

  override eq(other: CheckboxWidget) {
    return other.checked === this.checked;
  }

  toDOM() {
    const box = document.createElement('input');
    box.type = 'checkbox';
    box.className = 'checklist-box';
    box.checked = this.checked;
    box.tabIndex = -1;
    return box;
  }

  // its clicks come to the handlers below
  override ignoreEvent() {
    return false;
  }
}

const items = StateField.define<Item[]>({
  create: state => findItems(state.doc.toString()),
  update: (found, tr) =>
    tr.docChanged ? findItems(tr.state.doc.toString()) : found,
});

export const checklistItems = (state: EditorState) => state.field(items);

const boxes = (state: EditorState): DecorationSet =>
  Decoration.set(
    state.field(items).flatMap(item => [
      Decoration.line({
        class: item.checked
          ? 'checklist-item checklist-done'
          : 'checklist-item',
      }).range(item.line),
      Decoration.replace({ widget: new CheckboxWidget(item.checked) })
        .range(item.from, item.to),
    ]),
  );

const decorations = StateField.define<DecorationSet>({
  create: boxes,
  update: (set, tr) => tr.docChanged ? boxes(tr.state) : set,
  provide: field => [
    EditorView.decorations.from(field),
    EditorView.atomicRanges.of(view => view.state.field(field)),
  ],
});

const isBox = (target: EventTarget | null): target is HTMLInputElement =>
  target instanceof HTMLInputElement
  && target.classList.contains('checklist-box');

// the box toggles without moving the cursor, or, on a phone, bringing up the
// keyboard
const clicks = EditorView.domEventHandlers({
  mousedown: (e, view) => {
    if (!isBox(e.target)) return false;
    e.preventDefault();
    const pos = view.posAtDOM(e.target);
    const item = view.state.field(items).find(i => i.from === pos);
    if (item) {
      view.dispatch({ changes: toggle(item), userEvent: 'input.checklist' });
    }
    return true;
  },
  // the box's own toggle; the text's comes back through the decorations
  click: e => {
    if (!isBox(e.target)) return false;
    e.preventDefault();
    return true;
  },
});

const continueList: Command = view => {
  const { state } = view;
  const range = state.selection.main;
  if (!range.empty || state.selection.ranges.length > 1) return false;
  const line = state.doc.lineAt(range.head);
  const next = nextItem(line.text, range.head - line.from);
  if (!next) return false;
  view.dispatch({
    changes: {
      from: line.from + next.from,
      to: line.from + next.to,
      insert: next.insert,
    },
    selection: EditorSelection.cursor(line.from + next.cursor),
    scrollIntoView: true,
    userEvent: 'input',
  });
  return true;
};

// every item the selection touches, or none if it's on no item
const toggleSelected: Command = view => {
  const { state } = view;
  const lines = state.selection.ranges.flatMap(r => {
    const first = state.doc.lineAt(r.from).from;
    const last = state.doc.lineAt(r.to).from;
    return state.field(items).filter(i => i.line >= first && i.line <= last);
  });
  if (!lines.length) return false;
  view.dispatch({
    changes: [...new Set(lines)].map(toggle),
    userEvent: 'input.checklist',
  });
  return true;
};

export const checklists = [
  items,
  decorations,
  clicks,
  keymap.of([
    { key: 'Enter', run: continueList },
    { key: 'Mod-Enter', run: toggleSelected },
  ]),
];
